import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { getUsersUnderAdmin } from "../../../../api/project/Project";
import useAuth from "../../../../hooks/useAuth";
import ColorSelector from "./ColorSelector";

const ReminderFormFields = ({
  reminderDate,
  onReminderDateChange,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  isAllDay,
  onAllDayChange,
  selectedColor,
  colorAnchorEl,
  onColorMenuOpen,
  onColorMenuClose,
  onColorSelect,
  selectedUsers,
  onUsersChange,
  description,
  onDescriptionChange,
}) => {
  const [userList, setUserList] = useState([]);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getUsersUnderAdmin();
        if (res?.data?.success) {
          setUserList(res.data.data.peopleDropdown || []);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* Reminder Date with optional Time */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            gap: isAllDay ? 0 : 2,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              flex: isAllDay ? "1 1 100%" : "1 1 50%",
              transition: "flex 0.25s ease",
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Reminder date"
                value={reminderDate}
                onChange={onReminderDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Time Pickers - shown when not all day */}
          <Box
            sx={{
              flex: isAllDay ? "0 0 0%" : "1 1 50%",
              maxWidth: isAllDay ? 0 : "50%",
              opacity: isAllDay ? 0 : 1,
              transform: isAllDay ? "translateY(-4px)" : "translateY(0)",
              overflow: "hidden",
              pointerEvents: isAllDay ? "none" : "auto",
              display: "flex",
              gap: 1,
              alignItems: "center",
              transition:
                "flex 0.25s ease, max-width 0.25s ease, opacity 0.2s ease, transform 0.2s ease",
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <TimePicker
                label="Start time"
                value={startTime}
                onChange={onStartTimeChange}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    sx: { minWidth: 120 },
                  },
                }}
              />
            </LocalizationProvider>
            <Typography color="textSecondary">-</Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <TimePicker
                label="End time"
                value={endTime}
                onChange={onEndTimeChange}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    sx: { minWidth: 120 },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Grid>

      {/* All Day Toggle */}
      <Grid item xs={12}>
        <FormControlLabel
          control={<Switch checked={isAllDay} onChange={onAllDayChange} />}
          label="All day reminder"
        />
      </Grid>

      {/* Description */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Add description"
          placeholder="Add reminder description"
          multiline
          rows={3}
          variant="outlined"
          value={description || ""}
          onChange={(e) => onDescriptionChange?.(e.target.value)}
        />
      </Grid>

      {/* Assignees */}
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={userList}
          value={selectedUsers || []}
          onChange={(event, newValue) => {
            onUsersChange?.(newValue);
          }}
          getOptionLabel={(option) =>
            `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
            "Unknown"
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Remind"
              placeholder="Select users"
              variant="outlined"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={`${option.first_name || ""} ${option.last_name || ""}`.trim()}
                size="small"
              />
            ))
          }
        />
      </Grid>

      {/* Color Selector */}
      <Grid item xs={12}>
        <ColorSelector
          selectedColor={selectedColor}
          colorAnchorEl={colorAnchorEl}
          onColorMenuOpen={onColorMenuOpen}
          onColorMenuClose={onColorMenuClose}
          onColorSelect={onColorSelect}
          userName={
            currentUser
              ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim()
              : undefined
          }
        />
      </Grid>
    </Grid>
  );
};

ReminderFormFields.propTypes = {
  reminderDate: PropTypes.object,
  onReminderDateChange: PropTypes.func.isRequired,
  startTime: PropTypes.object,
  endTime: PropTypes.object,
  onStartTimeChange: PropTypes.func,
  onEndTimeChange: PropTypes.func,
  isAllDay: PropTypes.bool,
  onAllDayChange: PropTypes.func,
  selectedColor: PropTypes.string,
  colorAnchorEl: PropTypes.object,
  onColorMenuOpen: PropTypes.func,
  onColorMenuClose: PropTypes.func,
  onColorSelect: PropTypes.func,
  selectedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    })
  ),
  onUsersChange: PropTypes.func,
  description: PropTypes.string,
  onDescriptionChange: PropTypes.func,
};

ReminderFormFields.defaultProps = {
  isAllDay: true,
  selectedColor: "#FFC300",
  selectedUsers: [],
};

export default ReminderFormFields;
